import { blueGrey } from "@mui/material/colors";
import {
  BasketIcon,
  BillingIcon,
  Box,
  BuildingIcon,
  CompanyMappingIcon,
  ComplainsIcon,
  CreditCardIcon,
  CrmIcon,
  CustomAgreementIcon,
  CustomizeColumnIcon,
  DigioIcon,
  Email1Icon,
  EmployeeIcon,
  FAQsIcon,
  FlowIcon,
  GeneralPreferencesIcon,
  Grid2,
  ImportIcon,
  InvoiceIcon,
  KycIcon,
  List,
  ListItem,
  ListItemText,
  ManageIcon,
  MarketingMaterialIcon,
  OrderExcecutionIcon,
  PartnerIcon,
  PortfolioMetricsIcon,
  RightIssuesIcon,
  RiskProfileIcon,
  SheildIcon,
  TelegramIcon,
  Typography,
  UserIcon,
  WebsiteIcon,
  WhatsappIcon,
  styled,
} from "components";
import { UserInfoInterface } from "interfaces/User";
import DashboardLayout from "layouts/DashboardLayout";
import CompanyInformationPage from "Pages/CompanyInformation";
import Mandate from "Pages/Mandate";
import MarketingMaterial from "Pages/MarketingMaterial";
import MutualFundAutoImportSettings from "Pages/MutualFundAutoImport/MutualFundAutoImportSettings";
import PaymentGateway from "Pages/PaymentGateway";
import RightIssue from "Pages/RightIssue";
import CompanyMappingPreferences from "Pages/Settings/CompanyMappingPreferences";
import CustomAgreement from "Pages/Settings/CustomAgreement";
import EmailTemplateConfig from "Pages/Settings/Email Configuration/EmailTemplateConfig";
import EquityBasketSetting from "Pages/Settings/EquityBasketSetting";
import ExecutionSheet from "Pages/Settings/ExecutionSheet";
import GeneralPreferences from "Pages/Settings/GeneralPreferences";
import KYCRegistrationAgency from "Pages/Settings/KYCRegistrationAgency";
import RevenueManagerSettings from "Pages/Settings/RevenueManagerSettings";
import RiskProfiling from "Pages/Settings/RiskProfiling";
import SmtpSetup from "Pages/Settings/SmtpSetup";
import UserProfile from "Pages/Settings/UserProfile";
import { useGetCompanySettingsQuery } from "store/features/CompanySetting";
import { SETTING_TAB } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { AccountType, UserRoleTypes } from "utils/common";
import useDocumentTitle from "utils/useDocumentTitle";
import Billing from "./Billing";
import BrokerConfiguration from "./BrokerConfiguration";
import Digio from "./Digio";
import ComplainResolution from "./InvesterWebsite/ComplainResolution";
import ComplianceGrievance from "./InvesterWebsite/ComplianceGrievance";
import ComplianceSettings from "./InvesterWebsite/ComplianceSettings";
import FaqSettings from "./InvesterWebsite/FaqSettings";
import WebsiteConfigurations from "./InvesterWebsite/WebsiteConfigurations";
import WebsiteSettings from "./InvesterWebsite/WebsiteSettings";
import LedgerSettings from "./LedgerSettings";
import OnBoardingFlowTab from "./OnBoardingFlow";
import PortfolioMetrics from "./PortfolioMetrics";
import RoleWisePermission from "./RoleWisePermission";
import TelegramSetup from "./Telegram";
import Whatsapp from "./Whatsapp";

const Sidebar = styled(Box)(() => ({
  borderRight: `1px solid var(--neutral-100)`,
}));

const returnSettingTab = (userInfo: UserInfoInterface) => {
  const menuItems = [
    {
      text: "Account",
      children: [
        {
          text: "User Profile",
          component: "USER_PROFILE",
          show: true,
          icon: UserIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
        {
          text: "Company Profile",
          component: "COMPANY_PROFILE",
          show: true,
          icon: BuildingIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
        {
          text: "Billing",
          component: "BILLING",
          show: true,
          icon: BillingIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
      ],
    },
    {
      text: "Onboarding",
      children: [
        {
          text: "Risk Profiling",
          component: "RISK_PROFILING",
          show: true,
          icon: RiskProfileIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
        {
          text: "Custom Agreement",
          component: "CUSTOM_AGREEMENT",
          show: true,
          icon: CustomAgreementIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
        {
          text: "KRA/KYC",
          component: "KYC_REGISTRATION_AGENCY",
          show: true,
          icon: KycIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
        {
          text: "Onboarding Flow",
          component: "ONBOARDING_FLOW",
          show: true,
          icon: FlowIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
      ],
    },
    {
      text: "Subscription",
      children: [
        {
          text: "Website",
          component: "WEBSITE",
          show: userInfo?.broker?.isRaAccess,
          icon: WebsiteIcon,
          accountType: [AccountType.RA, AccountType.RA_RIA],
        },
        {
          text: "Configuration",
          component: "WEBSITE_CONFIGURATIONS",
          show: userInfo?.broker?.isRaAccess,
          icon: ManageIcon,
          accountType: [AccountType.RA, AccountType.RA_RIA],
        },
        {
          text: "Compliance",
          component: "COMPLIANCE",
          show: userInfo?.broker?.isRaAccess,
          icon: SheildIcon,
          accountType: [AccountType.RA, AccountType.RA_RIA],
        },
        {
          text: "FAQs",
          component: "FAQS",
          show: userInfo?.broker?.isRaAccess,
          icon: FAQsIcon,
          accountType: [AccountType.RA, AccountType.RA_RIA],
        },
        {
          text: "Complaints Resolution",
          component: "COMPLAIN_RESOLUTION",
          show: userInfo?.broker?.isRaAccess,
          icon: ComplainsIcon,
          accountType: [AccountType.RA, AccountType.RA_RIA],
        },
      ],
    },
    {
      text: "Preferences",
      children: [
        {
          text: "General Preferences",
          component: "GENERAL_PREFERENCES",
          show: true,
          icon: GeneralPreferencesIcon,
          accountType: [AccountType.RIA, AccountType.RA_RIA],
        },
        {
          text: "Portfolio Metrics",
          component: "PORTFOLIO_METRICS",
          show: true,
          icon: PortfolioMetricsIcon,
          accountType: [AccountType.RIA, AccountType.RA_RIA],
        },
        {
          text: "Invoice Preferences",
          component: "ANNEXURE_FOR_INVOICE",
          show: true,
          icon: InvoiceIcon,
          accountType: [AccountType.RIA, AccountType.RA, AccountType.RA_RIA],
        },
        {
          text: "Company Mapping",
          component: "COMPANY_MAPPING",
          show: true,
          icon: CompanyMappingIcon,
          accountType: [AccountType.RIA, AccountType.RA_RIA],
        },
        {
          text: "Right Issue",
          component: "RIGHT_ISSUE",
          show: true,
          icon: RightIssuesIcon,
          accountType: [AccountType.RIA, AccountType.RA_RIA],
        },
        {
          text: "Basket",
          component: "BASKET",
          show: true,
          icon: BasketIcon,
          accountType: [AccountType.RIA, AccountType.RA, AccountType.RA_RIA],
        },
        {
          text: "Order & Execution",
          component: "EXECUTION_SHEET",
          show: true,
          icon: OrderExcecutionIcon,
          accountType: [AccountType.RIA, AccountType.RA_RIA],
        },
        {
          text: "Marketing Material",
          component: "MARKETING_MATERIAL",
          show: true,
          icon: MarketingMaterialIcon,
          accountType: [AccountType.RIA, AccountType.RA_RIA],
        },
      ],
    },
    {
      text: "Roles & Permissions",
      children: [
        {
          text: "Employee",
          component: "EMPLOYEE",
          show: true,
          icon: EmployeeIcon,
          accountType: [AccountType.RIA, AccountType.RA, AccountType.RA_RIA],
        },
        {
          text: "Client",
          component: "CLIENT",
          show: true,
          icon: CrmIcon,
          accountType: [AccountType.RIA, AccountType.RA, AccountType.RA_RIA],
        },
        {
          text: "Partner",
          component: "PARTNER",
          show: true,
          icon: PartnerIcon,
          accountType: [AccountType.RIA, AccountType.RA_RIA],
        },
      ],
    },
    {
      text: "Integration & Setup",
      children: [
        {
          text: "Email Configuration",
          component: "EMAIL_TEMPLATE",
          show: true,
          icon: Email1Icon,
          accountType: [AccountType.RIA, AccountType.RA, AccountType.RA_RIA],
        },
        {
          text: "Payment Gateway",
          component: "PAYMENT_GATEWAY",
          show: true,
          icon: CreditCardIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
        {
          text: "Digio",
          component: "DIGIO",
          show: true,
          icon: DigioIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
        {
          text: "Whatsapp",
          component: "WHATSAPP",
          show: true,
          icon: WhatsappIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
        {
          text: "Telegram",
          component: "TELEGRAM",
          show: true,
          icon: TelegramIcon,
          accountType: [
            AccountType.RIA,
            AccountType.RA,
            AccountType.RA_RIA,
            AccountType.ONLY_ONBOARDING,
          ],
        },
      ],
    },
    {
      text: "Broker Configuration",
      children: [
        {
          text: "IIFL",
          component: "BROKER_CONFIGURATION",
          show: userInfo?.brokerSupport?.iiflSupport,
          icon: CustomizeColumnIcon,
          accountType: [AccountType.RIA, AccountType.RA_RIA],
        },
      ],
    },
    {
      text: "Mutual Fund",
      children: [
        {
          text: "Auto Import",
          component: "AUTO_IMPORT",
          show: true,
          icon: ImportIcon,
          accountType: [AccountType.RIA, AccountType.RA_RIA],
        },
      ],
    },
  ];

  return menuItems
    .map((item) => ({
      ...item,
      children: item.children
        .filter((child) => child.show)
        .filter((child) =>
          child.accountType?.includes(userInfo?.accountType as AccountType),
        ),
    }))
    .filter((item) => item.children.length > 0);
};

interface SidebarPropsInterface {
  dispatch: any;
  settingTab: {
    type: string;
    name: string;
  };
  userInfo: UserInfoInterface;
}

const SidebarMenu = (props: SidebarPropsInterface) => {
  const { dispatch, settingTab, userInfo } = props;

  return (
    <Sidebar>
      <List sx={{ pr: 2 }}>
        {returnSettingTab(userInfo).map((item, index) => {
          return (
            item.children.some((dataItem) => dataItem.show) && (
              <div key={index}>
                <Typography
                  mt={1}
                  fontSize={14}
                  fontWeight={500}
                  color={"#364152"}
                  px={2}
                  py={1}
                >
                  {item?.text}
                </Typography>
                {item?.children &&
                  item?.children?.map((item) => {
                    return (
                      item.show && (
                        <ListItem
                          key={item.text}
                          onClick={() => {
                            dispatch(
                              SETTING_TAB({
                                name: item.text,
                                type: item.component,
                              }),
                            );
                          }}
                          sx={{
                            backgroundColor:
                              settingTab.type === item.component
                                ? `var(--primary-50)`
                                : "",
                            color:
                              settingTab.type === item.component
                                ? `var(--primary-900)`
                                : "",
                            borderRadius: "8px",
                            ":hover": {
                              cursor: "pointer",
                            },
                          }}
                        >
                          <item.icon
                            width={16}
                            height={16}
                            color={
                              settingTab.type === item.component
                                ? `var(--primary-900)`
                                : blueGrey[500]
                            }
                          />
                          &nbsp;&nbsp;
                          <ListItemText
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              color:
                                settingTab.type === item.component
                                  ? `var(--primary-900)`
                                  : "var(--neutral-500)",
                              "& .MuiTypography-root": {
                                lineHeight: "16px !important",
                              },
                            }}
                            primary={item.text}
                          />
                        </ListItem>
                      )
                    );
                  })}
              </div>
            )
          );
        })}
      </List>
    </Sidebar>
  );
};

const AccountSetting = () => {
  useDocumentTitle("Settings");

  const sidebarOpen = useAppSelector((state) => state?.app?.sidebarOpen);
  const settingTab = useAppSelector((state) => state?.app?.settingTab);
  const userData = useAppSelector((state) => state?.user?.userData);

  const dispatch = useAppDispatch();

  const { data } = useGetCompanySettingsQuery(userData?.companyId);

  const renderComponent = () => {
    switch (settingTab.type) {
      case "USER_PROFILE":
        return <UserProfile />;
      case "COMPANY_PROFILE":
        return <CompanyInformationPage />;
      case "RISK_PROFILING":
        return <RiskProfiling />;
      case "CUSTOM_AGREEMENT":
        return <CustomAgreement />;
      case "PORTFOLIO_METRICS":
        return <PortfolioMetrics data={data} companyId={userData?.companyId} />;
      case "ANNEXURE_FOR_INVOICE":
        return (
          <RevenueManagerSettings data={data} companyId={userData?.companyId} />
        );
      case "COMPANY_MAPPING":
        return <CompanyMappingPreferences />;
      case "RIGHT_ISSUE":
        return <RightIssue />;
      case "BASKET":
        return (
          <EquityBasketSetting data={data} companyId={userData?.companyId} />
        );
      case "GENERAL_PREFERENCES":
        return (
          <GeneralPreferences data={data} companyId={userData?.companyId} />
        );
      case "EXECUTION_SHEET":
        return <ExecutionSheet />;
      case "EMAIL_CONFIGURATION":
        return <SmtpSetup />;
      case "MARKETING_MATERIAL":
        return <MarketingMaterial />;
      case "EMPLOYEE":
        return <RoleWisePermission type="Employee" />;
      case "CLIENT":
        return <RoleWisePermission type="Client" />;
      case "PARTNER":
        return <RoleWisePermission type="Partner" />;
      case "PAYMENT_GATEWAY":
        return <PaymentGateway />;
      case "AUTO_IMPORT":
        return <MutualFundAutoImportSettings />;
      case "MANDATE":
        return <Mandate />;
      case "LEDGER":
        return <LedgerSettings data={data} companyId={userData?.companyId} />;
      case "BROKER_CONFIGURATION":
        return <BrokerConfiguration broker={"IIFL"} />;
      case "KYC_REGISTRATION_AGENCY":
        return <KYCRegistrationAgency />;
      case "BILLING":
        return <Billing />;
      case "ONBOARDING_FLOW":
        return <OnBoardingFlowTab />;
      case "WEBSITE":
        return <WebsiteSettings />;
      case "COMPLIANCE":
        return <ComplianceSettings />;
      case "WEBSITE_CONFIGURATIONS":
        return <WebsiteConfigurations />;
      case "DIGIO":
        return <Digio />;
      case "EMAIL_TEMPLATE":
        return <EmailTemplateConfig />;
      case "FAQS":
        return <FaqSettings />;
      case "COMPLIANCE_GRIEVANCE":
        return <ComplianceGrievance />;
      case "COMPLAIN_RESOLUTION":
        return <ComplainResolution />;
      case "WHATSAPP":
        return <Whatsapp />;
      case "TELEGRAM":
        return <TelegramSetup />;
      default:
        return <Typography variant="h4">Page not found!</Typography>;
    }
  };

  return (
    <DashboardLayout title="Account Setting">
      <Grid2 container display={"flex"}>
        {userData?.userRole === UserRoleTypes.advisor && (
          <Grid2
            width={sidebarOpen ? "21%" : "20%"}
            height={"calc(100vh - 100px)"}
            overflow={"auto"}
          >
            <SidebarMenu
              dispatch={dispatch}
              settingTab={settingTab}
              userInfo={userData}
            />
          </Grid2>
        )}
        <Grid2
          size={
            (userData?.userRole === UserRoleTypes.client ||
              userData?.userRole === UserRoleTypes.partner) &&
            12
          }
          width={sidebarOpen ? "79%" : "80%"}
          p={"8px 16px"}
          height={"calc(100vh - 100px)"}
          overflow={"auto"}
        >
          <Typography fontSize={24} fontWeight={500} mb={3}>
            {settingTab.name}
          </Typography>
          {renderComponent()}
        </Grid2>
      </Grid2>
    </DashboardLayout>
  );
};

export default AccountSetting;
